import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Check, ChevronDown, Filter } from "lucide-react";
import { cn } from "../../lib/utils";
import { Button } from "../ui/button";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "../ui/command";
import { Popover, PopoverContent, PopoverTrigger } from "../ui/popover";
import { matchesQuery, type TreeOption } from "./treeOptions";

interface TreeMultiSelectFilterProps {
  label: string;
  options: TreeOption[];
  selectedIds: string[];
  onChange: (ids: string[]) => void;
  // Shown under the search box, e.g. "Includes subcategories"
  footnote?: string;
  disabled?: boolean;
  className?: string;
}

/**
 * Multi-select filter over a tree (ticket categories, knowledge base collections), searchable by path. Picking a
 * parent filters on everything under it as well - the server expands it - so children aren't ticked automatically.
 */
export function TreeMultiSelectFilter({
  label,
  options,
  selectedIds,
  onChange,
  footnote,
  disabled,
  className,
}: TreeMultiSelectFilterProps) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");

  // Ref-backed selection so cmdk's cached onSelect handlers always see fresh state
  const selectedRef = useRef(selectedIds);
  useEffect(() => {
    selectedRef.current = selectedIds;
  }, [selectedIds]);

  const toggle = useCallback(
    (id: string) => {
      const current = selectedRef.current;
      onChange(current.includes(id) ? current.filter((v) => v !== id) : [...current, id]);
    },
    [onChange]
  );

  const selectedSet = useMemo(() => new Set(selectedIds), [selectedIds]);
  const searching = query.trim().length > 0;
  const visible = useMemo(() => options.filter((o) => matchesQuery(o, query)), [options, query]);
  const hasSelection = selectedIds.length > 0;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          role="combobox"
          aria-expanded={open}
          disabled={disabled}
          className={cn(
            "w-full justify-between transition-all duration-200",
            hasSelection && "border-primary border-2 bg-primary/5",
            className
          )}
        >
          <div className="flex items-center min-w-0">
            <Filter className={cn("mr-2 h-4 w-4 flex-shrink-0", hasSelection ? "text-primary" : "text-muted-foreground")} />
            <span className="truncate">
              {label}
              {hasSelection && ` (${selectedIds.length})`}
            </span>
          </div>
          <ChevronDown className="h-4 w-4 opacity-50 flex-shrink-0" />
        </Button>
      </PopoverTrigger>
      <PopoverContent
        className="w-[var(--radix-popover-trigger-width)] min-w-[280px] p-0"
        align="start"
        onCloseAutoFocus={(e) => e.preventDefault()}
      >
        <Command shouldFilter={false}>
          <CommandInput placeholder={`Search ${label.toLowerCase()}…`} value={query} onValueChange={setQuery} />
          {(hasSelection || footnote) && (
            <div className="flex items-center justify-between gap-2 px-2 py-1.5 border-b">
              <span className="text-xs text-muted-foreground">
                {hasSelection ? `${selectedIds.length} selected` : footnote}
              </span>
              {hasSelection && (
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-7 text-xs"
                  onClick={(e) => {
                    e.preventDefault();
                    onChange([]);
                  }}
                >
                  Clear
                </Button>
              )}
            </div>
          )}
          <CommandList>
            <CommandEmpty>Nothing found</CommandEmpty>
            <CommandGroup>
              {visible.map((option) => (
                <CommandItem
                  key={option.id}
                  value={option.id}
                  onSelect={() => toggle(option.id)}
                  className="cursor-pointer"
                >
                  <span
                    className="flex-1 truncate"
                    style={{ paddingLeft: searching ? 0 : `${(option.depth - 1) * 16}px` }}
                  >
                    {searching ? option.path : option.name}
                    {option.archived && <span className="text-muted-foreground"> (archived)</span>}
                  </span>
                  {selectedSet.has(option.id) && <Check className="h-4 w-4 flex-shrink-0 text-primary" />}
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
          {footnote && hasSelection && (
            <div className="px-2 py-1.5 border-t text-xs text-muted-foreground">{footnote}</div>
          )}
        </Command>
      </PopoverContent>
    </Popover>
  );
}
